/* ===========================================================
   pages/Files.jsx — "Open from computer"

   FilesPage: pick a file or a folder (or see recent ones). A folder
   shows a plain list of its documents. LocalDocPage: the normal editor
   on that file. Edits save straight back into the real file a moment
   after you stop typing, and if the file changes on disk (another
   program, another window) the change shows up here; if you were both
   editing the same spot, both versions are kept.

   Browsers without the File System Access API get a friendly note and
   upload/download instead.
   =========================================================== */

import { useEffect, useRef, useState } from 'preact/hooks';
import { store, mode } from '../state.js';
import { go } from '../lib/router.js';
import { supported, pickFile, pickFolder, listFolder, ensurePermission, kindOf, langOf, allowed, newKey, uploads, pickerTypes } from '../files/local.js';
import { DocSync } from '../sync/provider.js';
import { MemoryStore } from '../sync/memstore.js';
import { ydocFrom, toJSON, applyJSON } from '../editor/convert.js';
import { EditorView } from '../editor/EditorView.jsx';
import { Toolbar } from '../editor/Toolbar.jsx';
import { equationDialog } from '../editor/EquationDialog.jsx';
import { env } from '../editor/env.js';
import { createDoc } from '../sync/docs.js';
import { merge3 } from '../../shared/merge3.js';
import { textOf } from '../../shared/blocks.js';
import { saveBlob, safeName } from '../files/export.js';
import { Button, Icon, IconButton, Spinner, toast, timeAgo, Menu } from '../ui/ui.jsx';
import { ProtoCard, Proto } from '../ui/proto.jsx';
import { TopBar } from './Home.jsx';
import { Logo } from './DocPage.jsx';

async function parse(name, text) {
  const k = kindOf(name);
  const md = await import('../lib/markdown.js');
  if (k === 'md') return md.fromMarkdown(text);
  if (k === 'code') return md.fromCode(text, langOf(name));
  return md.fromText(text);
}

async function serialize(name, json) {
  const k = kindOf(name);
  if (k === 'md') return (await import('../lib/markdown.js')).toMarkdown(json);
  if (k === 'code') return (json.content || []).filter(n => n.type === 'codeBlock').map(n => textOf(n)).join('\n\n') || textOf(json);
  return `${textOf(json)}\n`;
}

export function FilesPage() {
  const dev = mode.value === 'dev';
  const [recent, setRecent] = useState([]);
  const [folder, setFolder] = useState(null);   // { rec, files }
  const [needs, setNeeds] = useState(null);

  const loadRecent = () => store.value?.listFiles().then(r => setRecent(r.sort((a, b) => b.lastOpened - a.lastOpened).slice(0, 30)));
  useEffect(() => { loadRecent(); }, []);

  const remember = async (handle, kind, extra = {}) => {
    const all = await store.value.listFiles();
    for (const r of all) if (r.kind === kind && await r.handle.isSameEntry?.(handle)) { await store.value.putFile({ ...r, lastOpened: Date.now() }); return r.key; }
    const key = newKey();
    await store.value.putFile({ key, kind, name: handle.name, handle, lastOpened: Date.now(), ...extra });
    return key;
  };

  const openFile = async () => {
    try {
      const h = await pickFile(dev);
      if (!allowed(h.name, dev)) { toast(dev ? 'That kind of file can\'t be opened as a doc.' : 'In Casual mode you can open .md and .txt files. Switch to Developer mode in Settings for code files.'); return; }
      go(`/local/${await remember(h, 'file')}`);
    } catch (err) { if (err.name !== 'AbortError') toast(err, { kind: 'error' }); }
  };

  const showFolder = async rec => {
    if (!await ensurePermission(rec.handle, false)) { setNeeds(rec); return; }
    setNeeds(null);
    setFolder({ rec, files: await listFolder(rec.handle, dev) });
  };

  const openFolder = async () => {
    try {
      const h = await pickFolder();
      const key = await remember(h, 'dir');
      showFolder({ key, handle: h, name: h.name });
      loadRecent();
    } catch (err) { if (err.name !== 'AbortError') toast(err, { kind: 'error' }); }
  };

  const openFromFolder = async f => go(`/local/${await remember(f.handle, 'file', { dir: folder.rec.key })}`);

  const upload = e => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!allowed(file.name, dev)) { toast('That kind of file can\'t be opened as a doc here.'); return; }
    file.text().then(text => {
      const key = `u${newKey()}`;
      uploads.set(key, { name: file.name, text });
      go(`/local/${key}`);
    });
  };

  return (
    <div class="home">
      <TopBar title="Open from computer" />
      <main id="main" class="home-main narrow">
        {!supported() ? (
          <div class="files-unsupported">
            <ProtoCard title="Your browser can't save straight back to files" text="Opening a file and saving edits right back into it works in Chrome and Edge. In this browser you can still open a file here, edit it, and download it when you're done, or save it into your docs." />
            <label class="btn primary upload-btn">
              <Icon name="upload" size={17} /><span>Open a file</span>
              <input type="file" accept={pickerTypes(dev).flatMap(t => Object.values(t.accept).flat()).join(',')} onChange={upload} hidden />
            </label>
          </div>
        ) : (
          <>
            <p class="lead">Open a document from your computer. It opens like any doc, and your edits save straight back into the file. Docs Protocol only sees what you pick.</p>
            <div class="row-gap big">
              <Button kind="primary" size="lg" icon="file" onClick={openFile}>Open a file</Button>
              <Button size="lg" icon="folder" onClick={openFolder}>Open a folder</Button>
            </div>
            <p class="hint">{dev ? 'Markdown, text and code files.' : 'Markdown (.md) and text (.txt) files.'}</p>

            {needs && (
              <div class="banner"><Icon name="lock" size={16} /><span>Your browser needs your OK to open "{needs.name}" again.</span><span class="grow" />
                <Button size="sm" kind="primary" onClick={async () => { if (await ensurePermission(needs.handle, true)) showFolder(needs); }}>Allow</Button></div>
            )}

            {folder && (
              <section class="folder">
                <h2 class="section-title"><Icon name="folder" size={18} /> {folder.rec.name}</h2>
                {!folder.files.length ? <p class="hint">No documents in this folder.</p> : (
                  <ul class="doclist">
                    {folder.files.map(f => (
                      <li class="docrow" key={f.name}>
                        <button type="button" class="docrow-main" onClick={() => openFromFolder(f)}>
                          <span class="docrow-icon"><Icon name={kindOf(f.name) === 'code' ? 'code' : 'doc'} size={20} /></span>
                          <span class="docrow-text"><span class="docrow-title">{f.name}</span></span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}

            {recent.length > 0 && (
              <section>
                <h2 class="section-title">Recent</h2>
                <ul class="doclist">
                  {recent.map(r => (
                    <li class="docrow" key={r.key}>
                      <button type="button" class="docrow-main" onClick={() => (r.kind === 'dir' ? showFolder(r) : go(`/local/${r.key}`))}>
                        <span class="docrow-icon"><Icon name={r.kind === 'dir' ? 'folder' : 'doc'} size={20} /></span>
                        <span class="docrow-text"><span class="docrow-title">{r.name}</span><span class="docrow-snippet">{r.kind === 'dir' ? 'Folder' : 'File'} · opened {timeAgo(r.lastOpened)}</span></span>
                      </button>
                      <IconButton icon="close" label={`Forget ${r.name}`} onClick={() => store.value.delFile(r.key).then(loadRecent)} />
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}

const STATUS = {
  loading: 'Opening…', saved: 'Saved to file', saving: 'Saving to file…', denied: 'Not saving: allow access',
  error: 'Couldn\'t save to file', upload: 'Not saved: download when done',
};

export function LocalDocPage({ fileKey }) {
  const [state, setState] = useState({ phase: 'loading' });
  const [status, setStatus] = useState('loading');
  const [editor, setEditor] = useState(null);
  const ctx = useRef({});

  useEffect(() => {
    env.mode = () => mode.value;
    env.autoRunPreviews = () => true;
    env.editEquation = opts => equationDialog(opts);
    env.notify = msg => toast(msg);
  }, []);

  const open = async (ask = false) => {
    try {
      let name, text, handle = null, lastModified = 0;
      if (fileKey.startsWith('u')) {
        const u = uploads.get(fileKey);
        if (!u) { setState({ phase: 'error', error: { status: 404 } }); return; }
        ({ name, text } = u);
      } else {
        const rec = await store.value.getFile(fileKey);
        if (!rec) { setState({ phase: 'error', error: { status: 404 } }); return; }
        handle = rec.handle;
        if (!await ensurePermission(handle, ask)) { setState({ phase: 'permission', name: rec.name }); return; }
        const f = await handle.getFile();
        name = rec.name; text = await f.text(); lastModified = f.lastModified;
      }
      const json = await parse(name, text);
      const sync = new DocSync({ docId: `local-${fileKey}`, store: new MemoryStore(), connect: null });
      await sync.start();
      applyJSON(sync.doc, json, 'dp:load-file');
      Object.assign(ctx.current, { handle, name, written: text, lastModified, dirty: false, sync, base: json });
      setStatus(handle ? 'saved' : 'upload');
      setState({ phase: 'ready', name, sync });
    } catch (err) {
      setState({ phase: 'error', error: err });
    }
  };
  useEffect(() => { open(false); return () => ctx.current.sync?.destroy(); }, [fileKey]);

  /* save back into the file a moment after typing stops */
  useEffect(() => {
    const c = ctx.current;
    if (state.phase !== 'ready' || !c.handle) return;
    let timer = null;
    const write = async () => {
      try {
        setStatus('saving');
        const json = toJSON(c.sync.doc);
        const text = await serialize(c.name, json);
        const w = await c.handle.createWritable();
        await w.write(text);
        await w.close();
        const f = await c.handle.getFile();
        Object.assign(c, { written: text, lastModified: f.lastModified, dirty: false, base: json });
        setStatus('saved');
      } catch (err) {
        setStatus(err?.name === 'NotAllowedError' ? 'denied' : 'error');
        toast(err, { kind: 'error' });
      }
    };
    const onUpdate = (_u, origin) => {
      if (origin === 'dp:file' || origin === 'dp:load-file') return;
      c.dirty = true;
      setStatus('saving');
      clearTimeout(timer);
      timer = setTimeout(write, 700);
    };
    c.sync.doc.on('update', onUpdate);

    /* watch the file for changes made elsewhere */
    const poll = setInterval(async () => {
      try {
        const f = await c.handle.getFile();
        if (f.lastModified <= c.lastModified) return;
        const text = await f.text();
        c.lastModified = f.lastModified;
        if (text === c.written) return;
        const theirs = await parse(c.name, text);
        if (!c.dirty) {
          applyJSON(c.sync.doc, theirs, 'dp:file');
          Object.assign(c, { written: text, base: theirs });
          toast(`"${c.name}" changed on your computer. Showing the new version.`);
        } else {
          const r = merge3(c.base, theirs, toJSON(c.sync.doc));
          applyJSON(c.sync.doc, r.doc, 'dp:file');
          c.base = theirs;
          c.written = text;
          toast(r.conflicts.length
            ? `"${c.name}" changed on your computer while you were editing the same part. I kept both versions so you can choose.`
            : `"${c.name}" changed on your computer. I merged it with your edits.`, { proto: true, ms: 8000 });
          clearTimeout(timer);
          timer = setTimeout(write, 700);
        }
      } catch { /* file moved or permission gone: the next save will say so */ }
    }, 2000);
    const flush = () => { if (c.dirty) { clearTimeout(timer); write(); } };
    window.addEventListener('pagehide', flush);
    return () => { clearTimeout(timer); clearInterval(poll); c.sync.doc.off('update', onUpdate); window.removeEventListener('pagehide', flush); flush(); };
  }, [state.phase]);

  if (state.phase === 'loading') return <div class="center-page"><Spinner label="Opening the file" /></div>;
  if (state.phase === 'permission') {
    return (
      <div class="center-page">
        <ProtoCard title={`Open "${state.name}" again?`} text="For your safety, your browser asks before Docs Protocol can open a file from your computer again."
          actions={<><Button onClick={() => go('/files')}>Not now</Button><Button kind="primary" onClick={() => open(true)}>Allow and open</Button></>} />
      </div>
    );
  }
  if (state.phase === 'error') {
    return <div class="center-page"><ProtoCard title="Couldn't open that file" text={state.error?.status === 404 ? 'I don\'t have that file any more. Pick it again from "Open from computer".' : undefined} error={state.error?.status === 404 ? undefined : state.error} actions={<Button kind="primary" onClick={() => go('/files')}>Open from computer</Button>} /></div>;
  }

  const c = ctx.current;
  const download = async () => {
    const text = await serialize(c.name, toJSON(c.sync.doc));
    saveBlob(new Blob([text], { type: 'text/plain' }), c.name || `${safeName('document')}.txt`);
  };
  const toDocs = async () => {
    const id = await createDoc({ title: c.name.replace(/\.[^.]+$/, ''), json: toJSON(c.sync.doc) });
    toast('Saved into your docs. That copy syncs to your account.');
    go(`/d/${id}`);
  };

  return (
    <div class="docpage">
      <header class="doc-top">
        <a href="/files" class="doc-back" aria-label="Back to Open from computer"><Logo /></a>
        <div class="doc-titlebox">
          <div class="doc-title static"><Icon name="computer" size={16} /> {state.name}</div>
          <div class="doc-sub">
            <span class={`status s-${status === 'saved' ? 'saved' : status === 'saving' ? 'saving' : 'offline'}`} role="status">
              <Icon name={status === 'saved' ? 'check' : status === 'saving' ? 'cloud' : 'info'} size={15} /><span class="status-text">{STATUS[status]}</span>
            </span>
            <span class="tag">On your computer</span>
          </div>
        </div>
        <div class="doc-actions">
          {status === 'upload' && <Button kind="primary" icon="download" onClick={download}>Download</Button>}
          <Menu align="right" label="File menu" trigger={<IconButton icon="more" label="More" />} items={[
            { label: 'Save a copy into my docs', icon: 'cloud', onSelect: toDocs },
            { label: 'Download a copy', icon: 'download', onSelect: download },
          ]} />
        </div>
      </header>
      <Toolbar editor={editor} mode={mode.value} docId={null} />
      <div class="doc-body">
        <main class="doc-scroll" id="main">
          <div class="page">
            <EditorView sync={state.sync} editable user={{ name: 'You', color: '#888' }} docId={null} mode={mode.value} onEditor={setEditor} />
          </div>
        </main>
      </div>
    </div>
  );
}
